import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PANEL_PINNED_FINDING_TYPES, type Finding, type FindingAction } from '@ideoz/core';
import { useConversation } from '../context/ConversationContext';

/**
 * Owns the Scope-Intelligence / findings concern for the conversation workspace
 * (extracted from `ConversationModule` in Phase 2). Reads everything from the
 * shared facade via `useConversation()`.
 *
 * Findings surface in two places:
 *  - **inline** under the assistant turn that produced them, but only when they
 *    first appear *after* a turn has streamed this session (the reliable "live"
 *    signal) — `inlineFindingsByMessageId`;
 *  - the durable **Scope Intelligence panel** for everything else (acted-on,
 *    the scope-readiness card, and — after reload/switch — findings we couldn't
 *    anchor to a live turn) — `panelFindings`.
 *
 * Must be called exactly once per workspace (holds state); the rail + message
 * list consume its return.
 */
export function useConversationFindings() {
  const { facade } = useConversation();
  const {
    currentConversationId,
    currentScopeId,
    currentDiscussionRoomId,
    currentProjectId,
    messages,
    isStreaming,
  } = facade.state;
  const durableFindings = facade.findings.findings;

  // Only the card being resolved is disabled (not every card) — tracked by id.
  const [resolvingFindingId, setResolvingFindingId] = useState<string | null>(null);
  const handleFindingAction = useCallback(
    async (findingId: string, action: FindingAction) => {
      setResolvingFindingId(findingId);
      try {
        await facade.actions.resolveFinding(findingId, action);
      } finally {
        setResolvingFindingId(null);
      }
    },
    [facade.actions]
  );

  // Live anchor map (findingId → messageId). A finding is anchored inline only
  // if it first appears *after a turn has streamed this session*. Findings that
  // arrive with no live turn yet (initial load / reload) are recorded as seen
  // but never anchored, so they fall to the panel.
  const liveAnchorsRef = useRef<Record<string, string>>({});
  const seenFindingIdsRef = useRef<Set<string>>(new Set());
  const liveTurnStartedRef = useRef(false);
  const [liveAnchors, setLiveAnchors] = useState<Record<string, string>>({});

  // Reset the live state only on a *real* switch to another conversation/scope —
  // NOT on the null→id transition when a brand-new conversation is first assigned
  // its id mid-turn. That transition previously wiped `liveTurnStartedRef` right
  // after the first message started streaming, so the first turn's finding never
  // anchored inline and silently fell to the panel. Tracking the previous ids and
  // ignoring the initial assignment keeps the live signal alive across it.
  const prevConversationIdRef = useRef<string | null>(currentConversationId ?? null);
  const prevScopeIdRef = useRef<string | null>(currentScopeId ?? null);
  useEffect(() => {
    const conv = currentConversationId ?? null;
    const scope = currentScopeId ?? null;
    const conversationSwitched =
      prevConversationIdRef.current !== null && prevConversationIdRef.current !== conv;
    const scopeSwitched = prevScopeIdRef.current !== null && prevScopeIdRef.current !== scope;
    prevConversationIdRef.current = conv;
    prevScopeIdRef.current = scope;
    if (conversationSwitched || scopeSwitched) {
      liveAnchorsRef.current = {};
      seenFindingIdsRef.current = new Set();
      liveTurnStartedRef.current = false;
      setLiveAnchors({});
    }
  }, [currentConversationId, currentScopeId]);

  // Any streamed turn this session marks findings that follow as "live".
  useEffect(() => {
    if (isStreaming) liveTurnStartedRef.current = true;
  }, [isStreaming]);

  useEffect(() => {
    const lastAssistantId = [...messages].reverse().find((m) => m.role === 'assistant')?.id;
    const next = { ...liveAnchorsRef.current };
    let changed = false;
    for (const f of durableFindings) {
      if (seenFindingIdsRef.current.has(f.id) || liveAnchorsRef.current[f.id]) continue;
      seenFindingIdsRef.current.add(f.id);
      if (liveTurnStartedRef.current && lastAssistantId) {
        next[f.id] = lastAssistantId;
        changed = true;
      }
    }
    if (changed) {
      liveAnchorsRef.current = next;
      setLiveAnchors(next);
    }
  }, [durableFindings, messages]);

  // Untouched findings = not acted on and not the scope-level readiness card.
  const untouchedFindings = useMemo(
    () =>
      durableFindings.filter(
        (f) =>
          !PANEL_PINNED_FINDING_TYPES.has(f.finding_type) &&
          f.resolution_actor !== 'user'
      ),
    [durableFindings]
  );

  // Live-anchored untouched findings, grouped under their turn's message.
  const inlineFindingsByMessageId = useMemo(() => {
    const map: Record<string, Finding[]> = {};
    for (const f of untouchedFindings) {
      const messageId = liveAnchors[f.id];
      if (messageId) (map[messageId] ??= []).push(f);
    }
    return map;
  }, [untouchedFindings, liveAnchors]);

  const inlineFindingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const arr of Object.values(inlineFindingsByMessageId)) {
      for (const f of arr) ids.add(f.id);
    }
    return ids;
  }, [inlineFindingsByMessageId]);

  // The panel shows everything NOT shown inline.
  const panelFindings = useMemo(
    () => durableFindings.filter((f) => !inlineFindingIds.has(f.id)),
    [durableFindings, inlineFindingIds]
  );

  // Open for scope conversations (that scope's findings) and for Discussion
  // Rooms (the startup-wide aggregate loaded by the page facade).
  const isFindingsOpen = Boolean(currentScopeId || currentDiscussionRoomId);
  const [isFindingsCollapsed, setIsFindingsCollapsed] = useState(false);
  const [includeDismissedFindings, setIncludeDismissedFindings] = useState(false);

  // Design Insights panel (separate feature; its render stays inline in the
  // module, but its open/fetch/close lifecycle lives here with the rest of the
  // right-rail state).
  const [isDesignInsightsPanelOpen, setIsDesignInsightsPanelOpen] = useState(false);
  const openDesignInsights = useCallback(async () => {
    setIsDesignInsightsPanelOpen(true);
    if (currentProjectId && currentConversationId) {
      await facade.designInsights.fetchInsights(currentProjectId, currentConversationId);
    }
  }, [currentProjectId, currentConversationId, facade.designInsights]);
  const closeDesignInsights = useCallback(() => setIsDesignInsightsPanelOpen(false), []);

  return {
    panelFindings,
    inlineFindingsByMessageId,
    resolvingFindingId,
    handleFindingAction,
    isFindingsOpen,
    isFindingsCollapsed,
    setIsFindingsCollapsed,
    includeDismissedFindings,
    setIncludeDismissedFindings,
    isDesignInsightsPanelOpen,
    openDesignInsights,
    closeDesignInsights,
  };
}

export type ConversationFindings = ReturnType<typeof useConversationFindings>;
